import { useState } from "react";
import { Hexagon, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

export function HoneyWaitlist() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.joinWaitlist({ name, email });
      setJoined(true);
      toast.success(res.already ? "You're already on the list — we'll be in touch!" : "You're on the list! First dibs on the 2027 harvest.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="honey" className="relative bg-[#2C221E] overflow-hidden" data-testid="honey-section">
      <div className="absolute inset-0 opacity-20">
        <img
          src="https://images.pexels.com/photos/36813185/pexels-photo-36813185.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#2C221E] via-[#2C221E]/90 to-[#2C221E]/60" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid md:grid-cols-12 gap-12 items-center">
        <Reveal className="md:col-span-7">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#D97706] font-semibold" data-testid="honey-overline">
            Coming Spring 2027
          </p>
          <h2 className="mt-4 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#FAF5EE] tracking-tight" data-testid="honey-heading">
            Our first honey harvest is on its way
          </h2>
          <p className="mt-5 text-base text-[#E6D7C3] leading-relaxed max-w-xl" data-testid="honey-text">
            The bees are hard at work. In Spring 2027 we'll pull our very first frames of raw, unfiltered
            McGee's Bees honey — and with more hives joining the apiary in 2027, there will be more to share
            every season after. Join the waitlist and you'll be first to know when jars go on sale.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            {["Raw & unfiltered", "From our own hives", "Limited first harvest"].map((t) => (
              <span key={t} className="flex items-center gap-2 text-sm text-[#E6D7C3]">
                <Hexagon className="w-4 h-4 text-[#D97706]" fill="#D97706" fillOpacity="0.25" /> {t}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal className="md:col-span-5" delay={0.15}>
          <div className="bg-[#FAF5EE] rounded-2xl p-8 shadow-2xl" data-testid="waitlist-card">
            {joined ? (
              <div className="text-center py-6" data-testid="waitlist-success">
                <div className="w-14 h-14 rounded-full bg-[#2D4A3E] mx-auto flex items-center justify-center">
                  <Check className="w-7 h-7 text-[#FAF5EE]" />
                </div>
                <h3 className="mt-5 font-serif-display text-2xl font-bold text-[#2C221E]">You're on the list!</h3>
                <p className="mt-3 text-sm text-[#5A4D44] leading-relaxed">
                  We'll buzz you the moment our Spring 2027 honey is ready to jar. Thank you for supporting our family's little apiary.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-serif-display text-2xl font-bold text-[#2C221E]" data-testid="waitlist-form-heading">Reserve your first jar</h3>
                <p className="mt-2 text-sm text-[#8C7A6B]">Leave your email and get first dibs on the 2027 harvest.</p>
                <form onSubmit={submit} className="mt-6 flex flex-col gap-4" data-testid="waitlist-form">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="waitlist-name-input"
                    className="w-full px-5 py-3.5 rounded-xl border border-[#E6D7C3] bg-white text-sm text-[#2C221E] placeholder-[#8C7A6B] focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    data-testid="waitlist-email-input"
                    className="w-full px-5 py-3.5 rounded-xl border border-[#E6D7C3] bg-white text-sm text-[#2C221E] placeholder-[#8C7A6B] focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="waitlist-submit-button"
                    className="w-full py-3.5 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-semibold text-sm transition-colors active:scale-[0.98] disabled:opacity-60"
                  >
                    {loading ? "Joining..." : "Join the Honey Waitlist"}
                  </button>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
